
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/layouts/AppLayout";
import WelcomeHeader from "@/components/dashboard/WelcomeHeader";
import ClientStatsCard from "@/components/dashboard/ClientStatsCard";
import MonthlyPromptCard from "@/components/dashboard/MonthlyPromptCard";
import ContentGenerationCard from "@/components/dashboard/ContentGenerationCard";

interface Client {
  id: string;
  client_name: string;
  last_contact_date: string;
}

export default function MainDashboard() {
  const navigate = useNavigate();

  // Query to fetch user profile - simplified
  const { data: userProfile, isLoading: profileLoading } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data } = await supabase
        .from('profiles')
        .select('first_name, name')
        .eq('id', user.id)
        .single();
      
      return data;
    }
  });

  // Query to check if user has any clients - simplified
  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data } = await supabase
        .from("clients")
        .select("id, client_name, last_contact_date")
        .eq("user_id", user.id)
        .order('last_contact_date', { ascending: false });
      
      return data as Client[];
    },
  });

  const handleGenerateContent = async (clientId: string, messageType: "email" | "text" | "both") => {
    navigate(`/client-directory?clientId=${clientId}`);
  };

  if (profileLoading || clientsLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            <span>Loading...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  const userName = userProfile?.first_name || userProfile?.name || "User";
  const clientCount = clients?.length || 0;

  return (
    <AppLayout>
      <div className="container mx-auto p-6 space-y-6">
        <WelcomeHeader 
          userName={userName}
          subscriptionTier="essentials"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ClientStatsCard clientCount={clientCount} />
        </div>

        <MonthlyPromptCard summary="Welcome to your dashboard!" />

        <ContentGenerationCard
          clients={clients || []}
          onGenerate={handleGenerateContent}
        />
      </div>
    </AppLayout>
  );
}
